import {
  Box,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Input,
  Label,
  Paragraph,
  Radio,
  Text,
  ToggleCard,
} from '@zillow/constellation-10';
import { IconBuildingFilled, IconBuildingsFilled } from '@zillow/constellation-icons';

import type { ChangeEvent, Dispatch } from 'react';
import type { UserProfileReducerTypes } from '..';

type Props = {
  userProfile: {
    firstName: string;
    lastName: string;
    isLandlord: boolean;
    isPropertyManager: boolean;
    propertyCount: string;
  };
  setUserProfile: Dispatch<{
    type: UserProfileReducerTypes;
    payload?: string;
  }>;
  errors: {
    firstName: boolean;
    lastName: boolean;
    propertyCount: boolean;
  };
};

const PersonalDetails = ({ userProfile, setUserProfile, errors }: Props) => {
  const { firstName, lastName, isLandlord, isPropertyManager, propertyCount } = userProfile;
  const toggleLandlord = () => setUserProfile({ type: 'TOGGLE_LANDLORD' });
  const togglePropertyManager = () => setUserProfile({ type: 'TOGGLE_PROPERTY_MANAGER' });
  const setFirstName = (e: ChangeEvent<HTMLInputElement>) =>
    setUserProfile({ type: 'firstName', payload: e.target.value });
  const setLastName = (e: ChangeEvent<HTMLInputElement>) =>
    setUserProfile({ type: 'lastName', payload: e.target.value });
  const setPropertyCount = (e: ChangeEvent<HTMLInputElement>) =>
    setUserProfile({ type: 'propertyCount', payload: e.target.value });

  return (
    <>
      <Heading level={4} css={{ marginTop: { base: '600', lg: '1200' } }}>
        First, tell us about yourself
      </Heading>

      <Paragraph css={{ marginTop: '400' }}>What&apos;s your name?</Paragraph>

      <Box css={{ display: 'flex', flexDirection: 'row', marginTop: '400', gap: '400' }}>
        <FormField
          label={<Label>First name</Label>}
          control={<Input value={firstName} onChange={setFirstName} />}
          error={errors.firstName}
          className="fs-mask"
          css={{ flex: 1 }}
        >
          {errors.firstName && <FormHelp>First name is required</FormHelp>}
        </FormField>
        <FormField
          label={<Label>Last name</Label>}
          control={<Input value={lastName} onChange={setLastName} />}
          error={errors.lastName}
          className="fs-mask"
          css={{ flex: 1 }}
        >
          {errors.lastName && <FormHelp>Last name is required</FormHelp>}
        </FormField>
      </Box>

      <Paragraph css={{ marginTop: { base: 'md', lg: 'lg' } }}>What best describes why you are here today?</Paragraph>

      <FormField
        control={
          <Box
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: { base: '400', lg: '200' },
              lg: {
                flexDirection: 'row',
              },
            }}
          >
            <ToggleCard
              tone="soft"
              elevated={false}
              selected={isLandlord}
              onClick={toggleLandlord}
              css={{
                position: 'relative',
                flex: 1,
                borderRadius: '8px',
                paddingBlock: '600',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'transparent',
                lg: {
                  paddingBlock: '800',
                  width: '246px',
                },
              }}
            >
              <Radio
                checked={isLandlord}
                css={{
                  position: 'absolute',
                  top: '200',
                  right: '200',
                }}
              />
              <Icon
                size="md"
                css={{ marginRight: '200', color: 'icon.accent.blue.hero' }}
                render={<IconBuildingFilled />}
              />
              <Text>I&apos;m a landlord</Text>
            </ToggleCard>
            <ToggleCard
              tone="soft"
              elevated={false}
              selected={isPropertyManager}
              onClick={togglePropertyManager}
              css={{
                position: 'relative',
                flex: 1,
                borderRadius: '8px',
                paddingBlock: '600',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'transparent',
                lg: {
                  paddingBlock: '800',
                  width: '246px',
                },
              }}
            >
              <Radio
                checked={isPropertyManager}
                css={{
                  position: 'absolute',
                  top: '200',
                  right: '200',
                }}
              />
              <Icon
                size="md"
                css={{ marginRight: '200', color: 'icon.accent.blue.hero' }}
                render={<IconBuildingsFilled />}
              />
              <Text>I&apos;m a property manager</Text>
            </ToggleCard>
          </Box>
        }
        css={{ marginTop: '600' }}
      />

      <Paragraph css={{ marginTop: { base: 'md', lg: 'lg' } }}>How many properties do you own or manage?</Paragraph>

      <FormField
        error={errors.propertyCount}
        label={<Label>Number of properties</Label>}
        control={<Input value={propertyCount} onChange={setPropertyCount} />}
        css={{ marginTop: '600' }}
      >
        {errors.propertyCount && <FormHelp>Enter a whole number between 0 and 1000</FormHelp>}
      </FormField>
    </>
  );
};

export default PersonalDetails;
